import { Logger } from "@moment/logging";

import { env } from "../env/env";

const logger = new Logger("pty");

export type PtySpawnOptions = {
	cols?: number;
	rows?: number;
	cwd?: string;
	env?: Record<string, string>;
};

export type PtyProcess = {
	pid: number;
	onData: (callback: (data: Uint8Array) => void) => { dispose: () => void };
	onExit: (callback: (info: { exitCode: number; signal?: number }) => void) => {
		dispose: () => void;
	};
	write: (data: string) => void;
	resize: (cols: number, rows: number) => void;
	kill: (signal?: string) => void;
};

export type PtyAPI = {
	spawn: (file: string, args: string[], options: PtySpawnOptions) => Promise<PtyProcess>;
};

interface TauriPtyOptions {
	name?: string;
	cols?: number;
	rows?: number;
	cwd?: string;
	env?: Record<string, string | undefined>;
}

interface TauriPtyProcess {
	pid: number;
	cols: number;
	rows: number;
	onData: (listener: (data: Uint8Array) => void) => { dispose: () => void };
	onExit: (listener: (info: { exitCode: number; signal?: number }) => void) => {
		dispose: () => void;
	};
	write: (data: string) => void;
	resize: (cols: number, rows: number) => void;
	kill: (signal?: string) => void;
}

type TauriPtySpawnFn = (
	file: string,
	args: string[] | string,
	options: TauriPtyOptions
) => TauriPtyProcess;

const tauriPtyAPI: PtyAPI = {
	spawn: async (file, args, options) => {
		logger.debug("Spawning PTY process", { file, args, options });

		const tauriPty = await import("tauri-pty");
		const spawnFn = tauriPty.spawn as TauriPtySpawnFn;

		// Ensure TERM is set for color support in production builds.
		// Production macOS apps don't inherit shell environment, so we must set it explicitly.
		const ptyEnv: Record<string, string> = {
			TERM: "xterm-256color",
			COLORTERM: "truecolor",
			...options.env,
		};

		const ptyOptions: TauriPtyOptions = {
			name: "xterm-256color",
			cols: options.cols ?? 80,
			rows: options.rows ?? 24,
			cwd: options.cwd,
			env: ptyEnv,
		};

		const pty = spawnFn(file, args, ptyOptions);

		return {
			pid: pty.pid,
			onData: (callback) => {
				return pty.onData(callback);
			},
			onExit: (callback) => {
				return pty.onExit(callback);
			},
			write: (data) => {
				pty.write(data);
			},
			resize: (cols, rows) => {
				pty.resize(cols, rows);
			},
			kill: (signal) => {
				pty.kill(signal);
			},
		};
	},
};

const noPtyAPI: PtyAPI = {
	spawn: async () => {
		logger.warn("PTY not available in non-desktop environment");
		throw new Error("PTY is only available in desktop app");
	},
};

let ptyInstance: PtyAPI | undefined;

export const getPty = (): PtyAPI => {
	if (ptyInstance) {
		return ptyInstance;
	}

	ptyInstance = env.isDesktopApp ? tauriPtyAPI : noPtyAPI;

	return ptyInstance;
};

export const resetPty = (): void => {
	ptyInstance = undefined;
};
